<template>
  <v-form ref="blockEditForm" v-model="isFormValid">
    <!-- Back Button -->
    <div class="back-btn" @click="navigateToIndex">
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M36 27H23.83L29.42 21.41L28 20L20 28L28 36L29.41 34.59L23.83 29H36V27Z" fill="#8C959D"/>
      </svg>
    </div>

    <v-row>
      <!-- Page Title -->
      <v-col cols="12">
        <h1>{{ pageTitle }}</h1>
      </v-col>

      <!-- Form Content -->
      <v-col cols="12" md="8">
        <!-- Language Tabs -->
        <v-tabs v-model="activeLanguageTab">
          <v-tab>RU</v-tab>
          <v-tab>UA</v-tab>
        </v-tabs>

        <!-- Language Tab Items -->
        <v-tabs-items v-model="activeLanguageTab">
          <!-- Russian Tab -->
          <v-tab-item :eager="true">
            <v-row>
              <v-col cols="12">
                <v-text-field
                    v-model="block.name"
                    label="Название блока"
                    :rules="nameRuRules"
                    required
                    outlined
                    @input="clearFieldError('name')"
                />
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Ukrainian Tab -->
          <v-tab-item :eager="true">
            <v-row>
              <v-col cols="12">
                <v-text-field
                    v-model="block.name_ua"
                    label="Назва блоку"
                    :rules="nameUkRules"
                    required
                    outlined
                    @input="clearFieldError('name_ua')"
                />
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>

        <v-divider class="my-6" />

        <!-- Block Settings -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-4">Настройки блока</h3>
          </v-col>

          <v-col cols="12" md="6">
            <!-- Orientation Select -->
            <v-select
                v-model="block.orientation"
                :items="orientationOptions"
                :rules="orientationRules"
                label="Ориентация"
                outlined
                required
                @change="clearFieldError('orientation')"
            >
            </v-select>
          </v-col>

          <v-col cols="12" md="6">
            <!-- Icon Input with Emoji Picker -->
            <v-text-field
                v-model="block.icon"
                label="Иконка (URL)"
                outlined
                hint="Выберите иконку из списка ниже"
                persistent-hint
                readonly
            >
              <template #append>
                <v-menu offset-y>
                  <template #activator="{ on, attrs }">
                    <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                    >
                      <v-icon>mdi-emoticon-happy-outline</v-icon>
                    </v-btn>
                  </template>

                  <v-card>
                    <v-card-text>
                      <div class="emoji-grid">
                        <v-btn
                            v-for="emoji in popularEmojis"
                            :key="emoji.url"
                            text
                            icon
                            @click="block.icon = emoji.url"
                        >
                          <img :src="emoji.url" width="28" height="28" style="object-fit:contain" />
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-menu>
              </template>
            </v-text-field>

            <!-- Icon Preview -->
            <div v-if="block.icon" class="icon-preview text-center pa-4">
              <img
                  :src="block.icon"
                  width="64"
                  height="64"
                  style="object-fit:contain"
              />
              <div class="text-caption grey--text">Превью иконки</div>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <v-switch
                v-model="block.is_active"
                label="Активный блок"
                color="primary"
            />
          </v-col>

          <v-col cols="12" md="6" v-if="block.id">
            <v-text-field
                v-model.number="block.sort_order"
                label="Порядок сортировки"
                type="number"
                outlined
                hint="Чем меньше число, тем выше в списке"
            />
          </v-col>
        </v-row>

        <v-divider class="my-6" />

        <!-- Products Section -->
        <v-row>
          <v-col cols="12">
            <div class="d-flex justify-space-between align-center mb-4">
              <h3>Товары в блоке</h3>
              <v-btn
                  color="primary"
                  @click="openProductPicker"
              >
                <v-icon left>mdi-plus</v-icon>
                Добавить товары
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12">
              <draggable
                  v-model="block.products"
                  handle=".drag-handle"
                  @end="updateProductsOrder"
                  class="horizontal-products"
              >
                <v-card
                    v-for="(product, index) in block.products"
                    :key="product.id"
                    class="product-card-horizontal mb-2"
                    outlined
                >
                  <v-card-text class="d-flex align-center">
                    <v-icon class="drag-handle mr-4" style="cursor: move">
                      mdi-drag-vertical
                    </v-icon>

                    <v-img
                        v-if="product.image"
                        :src="product.image"
                        max-width="80"
                        max-height="80"
                        class="mr-4"
                    />

                    <div class="flex-grow-1">
                      <div class="font-weight-bold">{{ product.name }}</div>
                      <div class="text-caption grey--text">
                        Артикул: {{ product.code || 'N/A' }}
                      </div>
                    </div>

                    <div class="mr-4">
                      <v-chip small>{{ product.price }} UAH</v-chip>
                    </div>

                    <v-btn
                        icon
                        color="error"
                        @click="removeProduct(index)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-card-text>
                </v-card>
              </draggable>


            <v-alert
                v-if="!block.products || block.products.length === 0"
                type="info"
                text
                class="mt-4"
            >
              Товары не добавлены. Нажмите "Добавить товары" для выбора.
            </v-alert>
          </v-col>
        </v-row>

        <v-divider class="my-6" />

        <!-- Action Buttons -->
        <v-row>
          <v-col>
            <v-btn
                :loading="isButtonLoading"
                :disabled="!isFormValid || isButtonLoading"
                color="primary"
                large
                @click="handleSubmit"
            >
              {{ submitButtonText }}
            </v-btn>

            <v-btn
                text
                large
                class="ml-2"
                @click="navigateToIndex"
            >
              Отмена
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Product Picker Dialog -->
    <v-dialog v-model="productPickerDialog" max-width="900">
      <v-card>
        <v-card-title>
          Выбор товаров
          <v-spacer />
          <v-text-field
              v-model="productSearch"
              append-icon="mdi-magnify"
              label="Поиск"
              single-line
              hide-details
              class="mt-0 pt-0 mr-4"
              placeholder="Начните вводить название товара..."
              clearable
          />
          <v-checkbox
              v-model="showHiddenProducts"
              label="Показать скрытые"
              hide-details
              class="mt-0 pt-0"
          />
        </v-card-title>

        <v-card-text style="max-height: 500px; overflow-y: auto">
          <!-- Индикатор загрузки -->
          <div v-if="loadingProducts && productCategories.length === 0" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
            <div class="mt-4 grey--text">Загрузка категорий...</div>
          </div>

          <!-- Список категорий с ленивой загрузкой товаров -->
          <div v-else>
            <div v-if="filteredCategories.length === 0" class="text-center py-8 grey--text">
              Категории не найдены
            </div>

            <v-expansion-panels v-model="expandedPanels" multiple class="mb-4">
              <v-expansion-panel
                  v-for="category in filteredCategories"
                  :key="category.id"
                  @change="onCategoryExpand(category)"
              >
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <span class="font-weight-bold">{{ category.name }}</span>
                    <v-chip
                        v-if="category.products && category.products.length > 0"
                        x-small
                        class="ml-2"
                        color="primary"
                        outlined
                    >
                      {{ category.products.length }}
                    </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <!-- Индикатор загрузки товаров в категории -->
                  <div v-if="category.loading" class="text-center py-4">
                    <v-progress-circular indeterminate size="24" color="primary" />
                  </div>

                  <v-data-table
                      v-else-if="category.products"
                      v-model="selectedProducts"
                      :headers="productHeaders"
                      :items="displayProducts[category.id] || []"
                      show-select
                      item-key="id"
                      hide-default-footer
                      disable-pagination
                      class="elevation-0"
                  >
                    <template #item.image="{ item }">
                      <v-img
                          v-if="item.main_image && item.main_image.url_for_list"
                          :src="item.main_image.url_for_list"
                          max-width="40"
                          max-height="40"
                          class="my-1"
                      />
                    </template>

                    <template #item.price="{ item }">
                      {{ item.price }} UAH
                    </template>
                  </v-data-table>
                  <div v-else class="text-center py-4 grey--text">
                    Нет товаров
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeProductPicker">
            Отмена
          </v-btn>
          <v-btn
              color="primary"
              @click="addSelectedProducts"
              :disabled="selectedProducts.length === 0"
          >
            Добавить ({{ selectedProducts.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
        v-model="notification.show"
        :timeout="notification.timeout"
        :color="notification.color"
        right
        top
    >
      {{ notification.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
            v-bind="attrs"
            text
            @click="notification.show = false"
        >
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>
  </v-form>
</template>

<script>
import CategoryService from '@/api/CategoryService'
import BlockService from '@/api/BlockService'
import ProductService from '@/api/ProductService'
import draggable from 'vuedraggable'

const NOTIFICATION_TIMEOUT = 2000
const VALIDATION_MESSAGES = {
  REQUIRED: 'Нужно заполнить'
}
const NOTIFICATION_MESSAGES = {
  UPDATE_SUCCESS: 'Блок успешно обновлен',
  UPDATE_ERROR: 'Ошибка при обновлении',
  CREATE_SUCCESS: 'Блок создан',
  CREATE_ERROR: 'Ошибка при создании'
}

export default {
  name: 'BlockEdit',

  components: {
    draggable
  },

  props: {
    id: {
      type: [String, Number],
      default: null,
    }
  },

  data() {
    return {
      // Form state
      isFormValid: true,
      isButtonLoading: false,
      activeLanguageTab: 0,

      // Orientation options
      orientationOptions: [
        { text: 'Горизонтальный', value: 'horizontal', icon: 'mdi-arrow-left-right' },
        { text: 'Вертикальный', value: 'vertical', icon: 'mdi-arrow-up-down' }
      ],

      // Popular emojis for quick selection
      popularEmojis: [
        { label: '🧊', url: 'https://nommy.od.ua/storage/emoji/ice_1f9ca.png' },
        { label: '🎂', url: 'https://nommy.od.ua/storage/emoji/birthday-cake_1f382.png' },
        { label: '🍽️', url: 'https://nommy.od.ua/storage/emoji/fork-and-knife-with-plate_1f37d-fe0f.png' },
        { label: '🥢', url: 'https://nommy.od.ua/storage/emoji/chopsticks_1f962.png' },
        { label: '🏖️', url: 'https://nommy.od.ua/storage/emoji/beach-with-umbrella_1f3d6-fe0f.png' },
        { label: '📅', url: 'https://nommy.od.ua/storage/emoji/calendar_1f4c5.png' },
        { label: '💥', url: 'https://nommy.od.ua/storage/emoji/collision_1f4a5.png' },
        { label: '🥤', url: 'https://nommy.od.ua/storage/emoji/cup-with-straw_1f964.png' },
        { label: '🔥', url: 'https://nommy.od.ua/storage/emoji/fire_1f525.png' },
        { label: '🫶', url: 'https://nommy.od.ua/storage/emoji/heart-hands_1faf6.png' },
        { label: '❤️‍🔥', url: 'https://nommy.od.ua/storage/emoji/heart-on-fire_2764-fe0f-200d-1f525.png' },
        { label: '👨‍🍳', url: 'https://nommy.od.ua/storage/emoji/man-cook_1f468-200d-1f373.png' },
        { label: '🆕', url: 'https://nommy.od.ua/storage/emoji/new-button_1f195.png' },
        { label: '❄️', url: 'https://nommy.od.ua/storage/emoji/snowflake_2744-fe0f.png' },
        { label: '⭐', url: 'https://nommy.od.ua/storage/emoji/star_2b50.png' },
        { label: '🎁', url: 'https://nommy.od.ua/storage/emoji/wrapped-gift_1f381.png' },
        { label: '🚧', url: 'https://nommy.od.ua/storage/emoji/construction_1f6a7.png' },
        { label: '⛈️', url: 'https://nommy.od.ua/storage/emoji/cloud-with-lightning-and-rain_26c8-fe0f.png' },
        // Добавляй новые в том же формате:
        // { label: 'Описание', url: 'https://...' },
      ],
      // Product picker
      productPickerDialog: false,
      productSearch: '',
      expandedPanels: [],
      availableProducts: [],
      selectedProducts: [],
      showHiddenProducts: false,
      loadingProducts: false,
      productCategories: [],
      searchTimeout: null,
      productHeaders: [
        { text: 'Фото', value: 'image', sortable: false },
        { text: 'Название', value: 'name', sortable: true },
        { text: 'Цена', value: 'price', sortable: true }
      ],

      // Notification state
      notification: {
        show: false,
        text: '',
        color: ''
      },
      NOTIFICATION_TIMEOUT,

      // Validation errors from server
      validationErrors: {},

      // Validation rules
      validationRules: {
        required: [value => !!value || VALIDATION_MESSAGES.REQUIRED]
      },

      // Main data object
      block: this.getInitialBlock()
    }
  },

  computed: {
    isEditMode() {
      return !!this.id
    },

    pageTitle() {
      return this.isEditMode
          ? 'Редактирование блока'
          : 'Создание блока'
    },

    submitButtonText() {
      return this.isEditMode ? 'Сохранить' : 'Создать'
    },

    // Dynamic validation rules that include server errors
    nameRuRules() {
      const rules = [...this.validationRules.required]
      if (this.validationErrors.name) {
        rules.push(() => this.validationErrors.name?.[0] || true)
      }
      return rules
    },

    nameUkRules() {
      const rules = [...this.validationRules.required]
      if (this.validationErrors.name_ua) {
        rules.push(() => this.validationErrors.name_ua?.[0] || true)
      }
      return rules
    },

    filteredCategories() {
      const search = this.productSearch ? this.productSearch.toLowerCase() : ''
      
      return this.productCategories.filter(category => {
        // Если поиска нет и показываем все, то категория подходит
        if (!search && this.showHiddenProducts) return true

        // Если товаров нет, фильтруем только по названию категории
        if (!category.products) {
          return !search || category.name.toLowerCase().includes(search)
        }

        // Проверяем наличие подходящих товаров
        const hasVisibleProducts = category.products.some(product => {
          const matchesSearch = !search || product.name.toLowerCase().includes(search)
          const matchesHidden = this.showHiddenProducts || product.hide === 0
          return matchesSearch && matchesHidden
        })

        return hasVisibleProducts || (search && category.name.toLowerCase().includes(search))
      })
    },

    displayProducts() {
      const search = this.productSearch ? this.productSearch.toLowerCase() : ''
      const result = {}
      
      this.filteredCategories.forEach(category => {
        if (!category.products) return
        
        result[category.id] = category.products.filter(product => {
          const matchesSearch = !search || product.name.toLowerCase().includes(search)
          const matchesHidden = this.showHiddenProducts || product.hide === 0
          return matchesSearch && matchesHidden
        })
      })
      
      return result
    },

    orientationRules() {
      const rules = [...this.validationRules.required]
      if (this.validationErrors.orientation) {
        rules.push(() => this.validationErrors.orientation?.[0] || true)
      }
      return rules
    }
  },


  mounted() {
    if (this.isEditMode) {
      this.loadBlock()
    }
  },

  methods: {
    getInitialBlock() {
      return {
        name: '',
        name_ua: '',
        orientation: 'horizontal',
        icon: '📦',
        is_active: true,
        sort_order: 0,
        products: []
      }
    },

    async loadBlock() {
      try {
        const response = await BlockService.get(this.id)
        this.block = {
          ...response.data,
          products: response.data.products || []
        }
      } catch (error) {
        this.showNotification(NOTIFICATION_MESSAGES.UPDATE_ERROR, 'error')
      }
    },

    async loadAvailableProducts() {
      if (this.productCategories.length > 0) return

      this.loadingProducts = true

      try {
        const response = await CategoryService.list()
        this.productCategories = response.data.map(cat => ({
          ...cat,
          products: null,
          loading: false
        }))
      } catch (error) {
        console.error('Error loading categories:', error)
        this.productCategories = []
      } finally {
        this.loadingProducts = false
      }
    },

    async loadProductsForCategory(category) {
      if (category.products || category.loading) return

      this.$set(category, 'loading', true)

      try {
        const params = {
          page: 1,
          perPage: 500,
          categories: [category.id],
          withChildren: true
        }
        const response = await ProductService.list(params)
        this.$set(category, 'products', response.data.data)
      } catch (error) {
        console.error(`Error loading products for category ${category.id}:`, error)
      } finally {
        this.$set(category, 'loading', false)
      }
    },

    onCategoryExpand(category) {
      this.loadProductsForCategory(category)
    },


    navigateToIndex() {
      this.$router.push({ name: 'block-index' })
    },

    // Product management
    openProductPicker() {
      this.selectedProducts = [...this.block.products]
      this.productSearch = ''
      this.expandedPanels = []
      this.productPickerDialog = true
      this.loadAvailableProducts()
    },

    closeProductPicker() {
      this.productPickerDialog = false
      this.productSearch = ''
      this.selectedProducts = []
    },

    addSelectedProducts() {
      this.block.products = [...this.selectedProducts]
      this.closeProductPicker()
      this.updateProductsOrder()
    },

    removeProduct(index) {
      this.block.products.splice(index, 1)
      this.updateProductsOrder()
    },

    updateProductsOrder() {
      this.block.products = this.block.products.map((product, index) => ({
        ...product,
        sort_order: index
      }))
    },

    // Form validation and submission
    validateForm() {
      this.$refs.blockEditForm.validate()
      this.switchToRequiredLanguageTab()
    },

    clearFieldError(fieldName) {
      if (this.validationErrors[fieldName]) {
        this.$delete(this.validationErrors, fieldName)
      }
    },

    clearAllErrors() {
      this.validationErrors = {}
    },

    handleValidationErrors(error) {
      if (error.response && error.response.status === 422) {
        const errors = error.response.data.errors
        this.validationErrors = errors

        // Формуємо повідомлення про помилки
        const errorMessages = Object.keys(errors).map(field => {
          return errors[field].join(', ')
        })

        this.showNotification(
            'Ошибка валидации: ' + errorMessages.join('; '),
            'error'
        )

        // Перемикаємось на вкладку з помилкою
        if (errors.name) {
          this.activeLanguageTab = 0
        } else if (errors.name_ua) {
          this.activeLanguageTab = 1
        }

        // Викликаємо валідацію форми щоб показати помилки
        this.$nextTick(() => {
          this.$refs.blockEditForm.validate()
        })
      } else {
        this.showNotification('Ошибка при сохранении', 'error')
      }
    },

    switchToRequiredLanguageTab() {
      const hasRussianName = !!this.block.name
      const hasUkrainianName = !!this.block.name_ua

      if (!hasRussianName && hasUkrainianName) {
        this.activeLanguageTab = 0
      } else if (hasRussianName && !hasUkrainianName) {
        this.activeLanguageTab = 1
      }
    },

    handleSubmit() {
      this.clearAllErrors()
      this.validateForm()

      if (!this.isFormValid) {
        return
      }

      if (this.isEditMode) {
        this.updateBlock()
      } else {
        this.createBlock()
      }
    },

    async updateBlock() {
      this.isButtonLoading = true

      try {
        const data = this.getFormData()
        await BlockService.update(this.block.id, data)
        this.clearAllErrors()
        this.showNotification(NOTIFICATION_MESSAGES.UPDATE_SUCCESS, 'success')
      } catch (error) {
        this.handleValidationErrors(error)
      } finally {
        this.isButtonLoading = false
      }
    },

    async createBlock() {
      this.isButtonLoading = true

      try {
        const data = this.getFormData()
        const response = await BlockService.create(data)

        this.block.id = response.data.id
        this.clearAllErrors()
        this.showNotification(NOTIFICATION_MESSAGES.CREATE_SUCCESS, 'success')

        this.$router.push({
          name: 'block-edit',
          params: { id: response.data.data.id }
        })
      } catch (error) {
        this.handleValidationErrors(error)
      } finally {
        this.isButtonLoading = false
      }
    },

    getFormData() {
      return {
        name: this.block.name,
        name_ua: this.block.name_ua,
        orientation: this.block.orientation,
        icon: this.block.icon,
        is_active: this.block.is_active,
        sort_order: this.block.sort_order,
        products: this.block.products.map((product, index) => ({
          products_id: product.id,
          sort_order: index
        }))
      }
    },

    showNotification(text, color) {
      this.notification = {
        show: true,
        text,
        color
      }
    }
  }
}
</script>

<style scoped lang="scss">
.back-btn {
  cursor: pointer;
  display: inline-block;
  margin-bottom: 16px;

  &:hover {
    opacity: 0.7;
  }
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
}

.icon-preview {
  background: #f5f5f5;
  border-radius: 8px;
  margin-top: 8px;
}

.sticky-preview {
  position: sticky;
  top: 20px;
}

.preview-container {
  min-height: 200px;
}

.horizontal-products {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.vertical-products-container {
  max-width: 400px;
}

.vertical-products {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.product-card-vertical {
  position: relative;
}

.drag-handle {
  cursor: move;
}
</style>